import { useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  useValidateGiftCodeMutation,
  useFetchStoreForGiftQuery,
  useRedeemGiftCourseMutation,
} from './giftSelectionApi';

/**
 * Extract the code from URL params
 * URL format: /gift-select/:storeName?code=CODE&type=gift|invitation&design=a|b|c
 */
export const useCodeFromUrl = () => {
  const { storeName } = useParams();
  const [searchParams] = useSearchParams();

  const code = searchParams.get('code') || '';
  const codeType = searchParams.get('type') || 'gift'; // 'gift' or 'invitation'

  return {
    code,
    codeType,
    storeName: storeName || '',
  };
};

/**
 * Main hook for gift selection page logic
 *
 * Flow:
 * 1. User sees page and courses immediately (no validation)
 * 2. User browses and selects a course
 * 3. User confirms selection → validate code AND redeem in one step
 */
export const useGiftSelection = () => {
  const navigate = useNavigate();
  const { code, codeType, storeName } = useCodeFromUrl();

  // Local state
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // RTK Query hooks
  const [validateCode, { isLoading: isValidating }] = useValidateGiftCodeMutation();
  const [redeemCourse, { isLoading: isRedeeming }] = useRedeemGiftCourseMutation();

  // Fetch store data for branding (this happens on mount for branding purposes)
  const {
    data: storeData,
    isLoading: isLoadingStore,
    error: storeError,
  } = useFetchStoreForGiftQuery(
    { storeName },
    { skip: !storeName }
  );

  // Select a course
  const handleSelectCourse = useCallback((course) => {
    setSelectedCourse(course);
    // Clear any previous validation errors when selecting a new course
    setValidationError(null);
  }, []);

  // Confirm and redeem the selected course
  // This is where we validate AND redeem in one flow
  const handleConfirmSelection = useCallback(async (course) => {
    const courseToRedeem = course || selectedCourse;

    if (!courseToRedeem) {
      return { success: false, error: 'No course selected' };
    }

    if (!code) {
      setValidationError('No gift code provided');
      return { success: false, error: 'No gift code provided' };
    }

    try {
      // Step 1: Validate the code first
      const validationResult = await validateCode({ code, codeType }).unwrap();

      // Check if code is valid, not used, not expired
      const codeData = validationResult?.[0] || validationResult;

      if (codeData?.used) {
        const error = 'This gift code has already been used';
        setValidationError(error);
        return { success: false, error };
      }

      if (codeData?.expired) {
        const error = 'This gift code has expired';
        setValidationError(error);
        return { success: false, error };
      }

      // Step 2: Redeem the course
      await redeemCourse({
        code,
        codeType,
        courseId: courseToRedeem.id,
      }).unwrap();

      // Navigate to dashboard or course view on success
      navigate('/dashboard');
      return { success: true };
    } catch (error) {
      const errorMsg = error?.data?.response?.errMsg || error?.message || 'Failed to redeem gift. Please check your code and try again.';
      setValidationError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [code, codeType, selectedCourse, validateCode, redeemCourse, navigate]);

  // Computed state
  const isLoading = isLoadingStore;
  const isProcessing = isValidating || isRedeeming;

  const store = useMemo(() => ({
    name: storeData?.owner?.name || storeName,
    theme: storeData?.theme || {},
    owner: storeData?.owner || {},
    ...storeData,
  }), [storeData, storeName]);

  return {
    // URL params
    code,
    codeType,
    storeName,

    // State
    selectedCourse,
    validationError,
    store,

    // Loading states
    isLoading,
    isLoadingStore,
    isProcessing, // True when validating or redeeming
    isValidating,
    isRedeeming,

    // Errors
    storeError,

    // Actions
    handleSelectCourse,
    handleConfirmSelection,
    clearError: () => setValidationError(null),
  };
};

/**
 * Hook for accessing gift selection state
 */
export const useGiftSelectionState = () => {
  const { code, codeType, storeName } = useCodeFromUrl();

  return {
    code,
    codeType,
    storeName,
  };
};

export default useGiftSelection;
